import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, Download, RefreshCw } from 'lucide-react';
import { format, addMonths, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { api } from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { downloadCsv } from '../utils/csv';

interface Props {
  professionalId: string;
  /** embedded in another screen: the parent picks the professional and the month */
  fixedMonth?: Date;
  lockProfessional?: boolean;
}

interface CostSummary {
  total_hours: number;
  hourly_rate: number;
  total_cost: number;
  discount_percentage: number;
  discount_amount: number;
  final_cost: number;
  deduction_rules: Array<{ minHoursPerDay: number; discountPercentage: number; description?: string }>;
  by_room: Array<{ room_id: number; room_name: string; hourly_rate: number; hours: number; base_cost: number; discount: number; final_cost: number }>;
  by_day: Array<{ date: string; room_id: number; room_name: string; hours: number; base_cost: number; discount_percentage: number; discount: number; final_cost: number }>;
  payment: { paid: true; paidAt: string; amount: number; note: string | null; changedSincePaid: boolean } | null;
}

const money = (n: number) => new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(Math.round(n));

/**
 * What a professional owes for rented rooms in a month. Every figure comes from one server-side
 * calculation (rented hours x room rate, minus the daily discount), so this screen can never
 * disagree with itself or with what the admin sees.
 */
const ProfessionalMonthlySummary: React.FC<Props> = ({ professionalId: propProfessionalId, fixedMonth, lockProfessional }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [ownDate, setCurrentDate] = useState(new Date());
  const currentDate = fixedMonth ?? ownDate;
  const [professionalsList, setProfessionalsList] = useState<any[]>([]);
  const [selectedProfId, setSelectedProfId] = useState(propProfessionalId);
  const [cost, setCost] = useState<CostSummary | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');

  const year = useMemo(() => currentDate.getFullYear(), [currentDate]);
  const month = useMemo(() => currentDate.getMonth() + 1, [currentDate]);

  useEffect(() => { if (propProfessionalId) setSelectedProfId(propProfessionalId); }, [propProfessionalId]);

  // admin: dropdown with every professional
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/admin/professionals')
      .then(r => {
        setProfessionalsList(r.data || []);
        if (r.data?.length && !selectedProfId) setSelectedProfId(String(r.data[0].id));
      })
      .catch(() => toast.error('No pudimos cargar la lista de profesionales'));
  }, [isAdmin, selectedProfId]);

  const load = useCallback(async () => {
    if (!selectedProfId) return;
    setState('loading');
    try {
      const url = isAdmin
        ? `/admin/professionals/${selectedProfId}/monthly-cost/${year}/${month}`
        : `/postgres/professionals/profile/availability/${year}/${month}/cost`;
      const r = await api.get(url);
      setCost(r.data);
      setState('ok');
    } catch {
      setCost(null);
      setState('error');
    }
  }, [selectedProfId, isAdmin, year, month]);
  useEffect(() => { load(); }, [load]);

  const [saving, setSaving] = useState(false);
  const setPaid = async (paid: boolean) => {
    if (!paid && !window.confirm('¿Deshacer el pago de este mes?')) return;
    setSaving(true);
    try {
      await api.put(`/admin/rental-payments/${selectedProfId}/${year}/${month}`, { paid });
      toast.success(paid ? 'Pago registrado' : 'Pago deshecho');
      await load();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo guardar el pago');
    } finally { setSaving(false); }
  };

  const exportCsv = () => {
    if (!cost) return;
    const who = isAdmin ? (professionalsList.find(p => String(p.id) === String(selectedProfId))?.user?.name || 'profesional') : (user?.name || 'profesional');
    downloadCsv(`alquileres-${year}-${String(month).padStart(2, '0')}.csv`, [
      [`Resumen de alquiler de consultorios - ${who} - ${String(month).padStart(2, '0')}/${year}`],
      ['Fecha', 'Consultorio', 'Horas', 'Costo base', 'Descuento %', 'Descuento', 'Total'],
      ...cost.by_day.map(d => [d.date, d.room_name, d.hours, Math.round(d.base_cost), d.discount_percentage, Math.round(d.discount), Math.round(d.final_cost)]),
      ['TOTAL', '', cost.total_hours, Math.round(cost.total_cost), '', Math.round(cost.discount_amount), Math.round(cost.final_cost)],
      ['Estado', cost.payment ? `Pagado el ${format(new Date(cost.payment.paidAt), 'dd/MM/yyyy')}` : 'Pendiente de pago'],
    ]);
  };

  const ruleText = cost?.deduction_rules?.length
    ? cost.deduction_rules.map(r => `${r.discountPercentage}% desde ${r.minHoursPerDay} h en un mismo día y consultorio`).join(' · ')
    : '';

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold text-gray-900">
            Resumen mensual · {format(currentDate, 'MMMM yyyy', { locale: es })}
          </h2>
          {isAdmin && !lockProfessional && professionalsList.length > 0 && (
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Profesional:
              <select
                value={selectedProfId}
                onChange={e => setSelectedProfId(e.target.value)}
                className="input h-9 w-auto"
              >
                {professionalsList.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.user?.name || 'Desconocido'}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className={`flex items-center gap-2 self-end md:self-auto ${fixedMonth ? 'hidden' : ''}`}>
          <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="btn btn-outline btn-md" aria-label="Mes anterior">←</button>
          <button onClick={() => setCurrentDate(new Date())} className="btn btn-secondary btn-md">Hoy</button>
          <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="btn btn-outline btn-md" aria-label="Mes siguiente">→</button>
          <button onClick={() => { load(); toast.success('Datos actualizados'); }} className="btn btn-outline btn-md" aria-label="Actualizar">
            <RefreshCw className="h-4 w-4" />
          </button>
          <button onClick={exportCsv} disabled={!cost || cost.total_hours === 0} className="btn btn-outline btn-md" aria-label="Descargar resumen en CSV" title="Descargar resumen en CSV">
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="card p-6">
        {state === 'loading' && <div className="animate-pulse rounded-xl bg-gray-100 h-28" />}
        {state === 'error' && <p className="text-gray-500 text-center py-8">No pudimos calcular el costo de este mes. Probá actualizar.</p>}
        {state === 'ok' && cost && cost.total_hours === 0 && (
          <p className="text-gray-500 text-center py-8">No hay consultorios reservados en {format(currentDate, 'MMMM yyyy', { locale: es })}.</p>
        )}

        {state === 'ok' && cost && cost.total_hours > 0 && (<>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-2xl font-bold text-gray-900">{cost.total_hours} h</div>
              <div className="text-sm text-gray-600">Horas reservadas</div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="text-xl sm:text-2xl font-bold text-gray-900">${money(cost.total_cost)}</div>
              <div className="text-sm text-gray-600">Costo base</div>
            </div>
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <div className="text-xl sm:text-2xl font-bold text-green-700">-${money(cost.discount_amount)}</div>
              <div className="text-sm text-green-700">Descuentos</div>
            </div>
            <div className="rounded-xl bg-primary-600 p-4 text-white">
              <div className="text-xl sm:text-2xl font-bold">${money(cost.final_cost)}</div>
              <div className="text-sm opacity-90">Total a pagar</div>
            </div>
          </div>
          {ruleText && <p className="text-xs text-gray-500 mb-4">Descuento por día: {ruleText}.</p>}

          <div className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 text-sm ${cost.payment ? 'border-green-200 bg-green-50 text-green-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
            <div className="flex items-start gap-2">
              {cost.payment ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" /> : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />}
              <div>
                <p className="font-semibold">
                  {cost.payment ? `Pagado el ${format(new Date(cost.payment.paidAt), "d 'de' MMMM", { locale: es })} · $${money(cost.payment.amount)}` : 'Pendiente de pago'}
                </p>
                {cost.payment?.changedSincePaid && <p>Alquilaste horas después del pago: ahora el total es ${money(cost.final_cost)}.</p>}
                {cost.payment?.note && <p className="opacity-80">{cost.payment.note}</p>}
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                {cost.payment && <button className="btn btn-sm btn-outline" disabled={saving} onClick={() => setPaid(false)}>Deshacer pago</button>}
                {(!cost.payment || cost.payment.changedSincePaid) && <button className="btn btn-sm btn-primary" disabled={saving} onClick={() => setPaid(true)}>{cost.payment ? 'Actualizar monto' : 'Marcar pagado'}</button>}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {cost.by_room.map(room => (
              <section key={room.room_id} className="rounded-xl border border-gray-200 p-5" aria-label={room.room_name}>
                <div className="flex justify-between items-baseline mb-1">
                  <h4 className="text-lg font-semibold">{room.room_name}</h4>
                  <div className="text-lg font-bold text-primary-700">${money(room.final_cost)}</div>
                </div>
                <p className="text-sm text-gray-500 mb-3">{room.hours} h a ${money(room.hourly_rate)} por hora</p>
                <ul className="space-y-1.5">
                  {cost.by_day.filter(d => d.room_id === room.room_id).map(d => (
                    <li key={d.date} className="flex justify-between gap-3 text-sm p-2 bg-gray-50 rounded-lg">
                      <span>
                        {format(new Date(`${d.date}T12:00:00`), "EEE d 'de' MMM", { locale: es })} · {d.hours} h
                        {d.discount_percentage > 0 && (
                          <span className="ml-2 rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-semibold">-{d.discount_percentage}%</span>
                        )}
                      </span>
                      <span className="font-medium">${money(d.final_cost)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>)}
      </div>
    </div>
  );
};

export default ProfessionalMonthlySummary;
